// Single source of truth for every route path in the app. The authenticated
// prefix appears exactly once, below, so moving the app to a different mount
// point is a one-line change.
export const APP_PREFIX = '/app';

const app = <S extends string>(segment: S) =>
  `${APP_PREFIX}/${segment}` as const;

export const PUBLIC_ROUTES = {
  LANDING: '/',
  CONTACT: '/contact',
  REQUEST_ACCESS: '/request-access'
} as const;

// Public legal pages, served from admin-published content. The path segment
// after /legal/ is the API slug, so these strings are the single source of
// truth shared by the router, the footer, and the auth-probe allow-list below.
export const LEGAL_ROUTES = {
  PRIVACY: '/legal/privacy',
  TERMS_OF_USE: '/legal/terms-of-use',
  // Standalone page, not one of the two admin-published :slug documents
  // above — deliberately absent from PUBLIC_ONLY_PATHS below, since it needs
  // the /auth/me probe to persist a signed-in visitor's choice server-side.
  PRIVACY_CHOICES: '/legal/privacy-choices'
} as const;

export type LegalSlug = 'privacy' | 'terms-of-use';

export const LOGIN_ROUTES = {
  ROOT: '/login',
  SPLAT: '/login/*',
  // Kept until the b2b-api change that points WorkOS errors at /login is live
  // in every environment. An older API still redirects to `${portal}/auth/login`.
  LEGACY_AUTH_SPLAT: '/auth/*'
} as const;

export const APP_SEGMENTS = {
  ROI_CALCULATOR: 'roi-calculator',
  PROFILE: 'profile',
  ORGANIZATION: 'organization',
  SETTINGS: 'settings',
  REPORTS: 'reports',
  ANALYTICS: 'analytics',
  BRAND_PERFORMANCE_THREDUP: 'brand-performance',
  GIFT_CARDS: 'gift-cards',
  PROGRAMS: 'programs',
  KNOWLEDGE_BASE: 'knowledge-base',
  KITS_ORDERING: 'bag-order',
  CASHOUT_TILE: 'cashout-tile',
  CLEANOUT_LANDING_BUILDER: 'cleanout-landing-builder',
  NEWSROOM: 'newsroom',
  DROPSHIPPING: 'dropshipping',
  NOT_FOUND: 'not-found'
} as const;

export const APP_ROUTES = {
  ROOT: APP_PREFIX || '/',
  ROI_CALCULATOR: app(APP_SEGMENTS.ROI_CALCULATOR),
  PROFILE: app(APP_SEGMENTS.PROFILE),
  ORGANIZATION: app(APP_SEGMENTS.ORGANIZATION),
  SETTINGS: app(APP_SEGMENTS.SETTINGS),
  REPORTS: app(APP_SEGMENTS.REPORTS),
  ANALYTICS: app(APP_SEGMENTS.ANALYTICS),
  BRAND_PERFORMANCE_THREDUP: app(APP_SEGMENTS.BRAND_PERFORMANCE_THREDUP),
  GIFT_CARDS: app(APP_SEGMENTS.GIFT_CARDS),
  PROGRAMS: app(APP_SEGMENTS.PROGRAMS),
  KNOWLEDGE_BASE: app(APP_SEGMENTS.KNOWLEDGE_BASE),
  KITS_ORDERING: app(APP_SEGMENTS.KITS_ORDERING),
  CASHOUT_TILE: app(APP_SEGMENTS.CASHOUT_TILE),
  CLEANOUT_LANDING_BUILDER: app(APP_SEGMENTS.CLEANOUT_LANDING_BUILDER),
  NEWSROOM: app(APP_SEGMENTS.NEWSROOM),
  DROPSHIPPING: app(APP_SEGMENTS.DROPSHIPPING),
  NOT_FOUND: app(APP_SEGMENTS.NOT_FOUND)
} as const;

// Sections removed from the portal whose URLs still arrive from bookmarks and
// old emails. Everything under one redirects to Programs instead of a 404.
// Onboarding moved to b2b-admin.
export const RETIRED_APP_SEGMENTS = ['onboarding'] as const;

export const DROPSHIPPING_SEGMENTS = {
  CATALOGUE: 'catalogue',
  LISTINGS: 'listings',
  WAREHOUSES: 'warehouses',
  ORDERS: 'orders'
} as const;

// Dropshipping mounts under a splat with v7_relativeSplatPath enabled, so its
// nav targets must be absolute. See src/pages/Dropshipping/constants.ts.
export const dropshippingPath = (segment: string): string =>
  `${APP_ROUTES.DROPSHIPPING}/${segment}`;

export const buildLoginPath = (redirectTo?: string): string =>
  redirectTo
    ? `${LOGIN_ROUTES.ROOT}?redirect=${encodeURIComponent(redirectTo)}`
    : LOGIN_ROUTES.ROOT;

export const isAppPath = (pathname: string): boolean =>
  pathname === APP_PREFIX || pathname.startsWith(`${APP_PREFIX}/`);

// Marketing pages that render no session-dependent UI. Nothing under
// src/pages/public imports useAuth.
const PUBLIC_ONLY_PATHS: string[] = [
  PUBLIC_ROUTES.LANDING,
  PUBLIC_ROUTES.CONTACT,
  PUBLIC_ROUTES.REQUEST_ACCESS,
  // Legal pages render no session-dependent UI and are reachable while logged
  // out; probing /auth/me on them would be a wasted credentialed round trip and
  // would mislabel the visitor in analytics. Exact-match list, so both
  // concrete paths are listed rather than a /legal prefix.
  LEGAL_ROUTES.PRIVACY,
  LEGAL_ROUTES.TERMS_OF_USE
];

const normalise = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

/**
 * Whether a route needs the /auth/me session probe on boot.
 *
 * Deliberately an allow-list of public paths rather than a deny-list of app
 * paths: a legacy route like `/reports` is not under `/app` yet still redirects
 * into it, so skipping the probe there would bounce a signed-in partner to
 * login. Anything unrecognised errs toward probing.
 */
export const needsAuthCheck = (pathname: string): boolean =>
  !PUBLIC_ONLY_PATHS.includes(normalise(pathname));

const LEGACY_PLAIN_SEGMENTS = [
  APP_SEGMENTS.ROI_CALCULATOR,
  APP_SEGMENTS.PROFILE,
  APP_SEGMENTS.ORGANIZATION,
  APP_SEGMENTS.SETTINGS,
  APP_SEGMENTS.REPORTS,
  APP_SEGMENTS.ANALYTICS,
  APP_SEGMENTS.BRAND_PERFORMANCE_THREDUP,
  APP_SEGMENTS.GIFT_CARDS,
  APP_SEGMENTS.PROGRAMS,
  APP_SEGMENTS.KNOWLEDGE_BASE,
  APP_SEGMENTS.KITS_ORDERING,
  APP_SEGMENTS.CASHOUT_TILE,
  APP_SEGMENTS.CLEANOUT_LANDING_BUILDER,
  APP_SEGMENTS.NOT_FOUND
];

// A trailing /* also matches its bare parent, so one entry per splat section
// covers the section index and every nested route beneath it.
const LEGACY_SPLAT_SEGMENTS = [
  ...RETIRED_APP_SEGMENTS,
  APP_SEGMENTS.DROPSHIPPING
];

export const LEGACY_APP_PATHS: string[] = [
  ...LEGACY_PLAIN_SEGMENTS.map((segment) => `/${segment}`),
  ...LEGACY_SPLAT_SEGMENTS.map((segment) => `/${segment}/*`)
];
